import {
  Box,
  Button,
  Dropdown,
  DropdownItem,
  InputField,
  Radio,
  Typography,
  getColor,
} from "fds";
import { useTranslation } from "react-i18next";
import utilsCommon from "../../shared/utils/common";
import { ExternalUser, User, UserDTO } from "../../types/adminDept";
import { PositionOrRoleDto } from "../../types/adminPositionRole";

type ViewState = "VIEW" | "UPDATE" | "INSERT";

interface PasswordState {
  password1: string;
  password2: string;
  type: string;
}

interface Props {
  /** 현재 뷰 상태 */
  viewState: ViewState;
  /** 선택된 노드 타입 */
  nodeType: string;
  /** 내부 사용자 정보 (VIEW 모드) */
  userInfo: any;
  editUserInfo: User & UserDTO;
  setEditUserInfo: (v: User & UserDTO) => void;
  /** 외부 사용자 정보 (VIEW 모드) */
  externalUserInfo: ExternalUser;
  editExternalUserInfo: ExternalUser;
  setEditExternalUserInfo: (v: ExternalUser) => void;
  positionList: PositionOrRoleDto[];
  roleList: PositionOrRoleDto[];
  buttonClick: number;
  changeButtonClick: (v: number) => void;
  password: PasswordState;
  setPassword: (v: PasswordState) => void;
  isNewPassword: boolean;
  setIsNewPassword: (v: boolean) => void;
  onChangePassword: () => void;
  ROOT_DEPT_CODE: string;
  companyName: string;
  leftTab: number;
}

// ────────────────────────────────────────────────────────────────────────────────
// 공통 필드 래퍼: 라벨 + 필수 표시 + 값/인풋
// ────────────────────────────────────────────────────────────────────────────────
const FieldRow: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, required, children }) => (
  <Box>
    <Box>
      <Typography
        type="subtitle2"
        exactColor={getColor("COLOR_TEXT_SECONDARY")}
      >
        {label}
      </Typography>
      {required && (
        <Typography exactColor={getColor("COLOR_TEXT_ERROR")} classes="mg ml-4">
          *
        </Typography>
      )}
    </Box>
    {children}
  </Box>
);

const ReadOnlyText: React.FC<{ value?: string | number }> = ({ value }) => (
  <Typography type="body2_rg" exactColor={getColor("COLOR_TEXT_SECONDARY")}>
    {value}
  </Typography>
);

// ────────────────────────────────────────────────────────────────────────────────
// 비밀번호 변경 섹션
// ────────────────────────────────────────────────────────────────────────────────
const PasswordSection: React.FC<{
  viewState: ViewState;
  isNewPassword: boolean;
  setIsNewPassword: (v: boolean) => void;
  password: PasswordState;
  setPassword: (v: PasswordState) => void;
  onChangePassword: () => void;
  sectionType?: "password" | "settingMethod" | "inputField";
  showChangeButton?: boolean; // 외부 사용자의 경우 조건부
}> = ({
  viewState,
  isNewPassword,
  setIsNewPassword,
  password,
  setPassword,
  onChangePassword,
  sectionType = password,
  showChangeButton = true,
}) => {
  const { t } = useTranslation();

  const resetPassword = () => {
    setIsNewPassword(false);
    setPassword({ password1: "", password2: "", type: "RANDOM" });
  };

  return (
    <>
      {sectionType === "password" && (
        <Box>
          {isNewPassword ? (
            <Box justifyContent="flex-end" width="100%">
              <Button
                btnStyle="fill"
                type="error"
                size="sm"
                label={t("admin.common.button.cancel")}
                onClick={resetPassword}
              />
              <Button
                classes="mg ml-8"
                btnStyle="fill"
                size="sm"
                label={t("admin.common.button.apply")}
                onClick={onChangePassword}
              />
            </Box>
          ) : showChangeButton ? (
            <Box justifyContent="flex-end" width="100%">
              <Button
                btnStyle="line"
                size="sm"
                label={t("admin.dept.newPassword")}
                onClick={() => setIsNewPassword(true)}
              />
            </Box>
          ) : null}
        </Box>
      )}

      {(viewState === "INSERT" || isNewPassword) && (
        <>
          {sectionType === "settingMethod" && (
            <Box direction="row">
              <Radio
                label={t("admin.dept.createManually")}
                check={password.type !== "RANDOM"}
                onClick={() =>
                  setPassword({ password1: "", password2: "", type: "" })
                }
              />
              <Radio
                label={t("admin.dept.createAuto")}
                check={password.type === "RANDOM"}
                onClick={() =>
                  setPassword({ password1: "", password2: "", type: "RANDOM" })
                }
                classes="mg ml-24"
              />
            </Box>
          )}

          {sectionType === "inputField" && password.type !== "RANDOM" && (
            <>
              <FieldRow label={t("admin.dept.password")} required>
                <InputField
                  type="password"
                  value={password.password1}
                  placeholder={t("admin.dept.inputPassword")}
                  onChange={(e) =>
                    setPassword({ ...password, password1: e.target.value })
                  }
                  width={240}
                  disabled={password.type === "RANDOM"}
                />
              </FieldRow>
              <FieldRow label={t("admin.dept.checkPassword")} required>
                <InputField
                  type="password"
                  value={password.password2}
                  placeholder={t("admin.dept.reInputPassword")}
                  onChange={(e) =>
                    setPassword({ ...password, password2: e.target.value })
                  }
                  width={240}
                  disabled={password.type === "RANDOM"}
                />
              </FieldRow>
            </>
          )}
        </>
      )}
    </>
  );
};

// ────────────────────────────────────────────────────────────────────────────────
// 내부 사용자 폼
// ────────────────────────────────────────────────────────────────────────────────
const InternalUserForm: React.FC<{
  viewState: ViewState;
  userInfo: any;
  editUserInfo: User & UserDTO;
  setEditUserInfo: (v: User & UserDTO) => void;
  positionList: PositionOrRoleDto[];
  roleList: PositionOrRoleDto[];
  buttonClick: number;
  changeButtonClick: (v: number) => void;
  password: PasswordState;
  setPassword: (v: PasswordState) => void;
  isNewPassword: boolean;
  setIsNewPassword: (v: boolean) => void;
  onChangePassword: () => void;
  ROOT_DEPT_CODE: string;
  companyName: string;
}> = ({
  viewState,
  userInfo,
  editUserInfo,
  setEditUserInfo,
  positionList,
  roleList,
  buttonClick,
  changeButtonClick,
  password,
  setPassword,
  isNewPassword,
  setIsNewPassword,
  onChangePassword,
  ROOT_DEPT_CODE,
  companyName,
}) => {
  const { t } = useTranslation();
  const isOCI = window.eco.serviceType === "OCI";
  const isView = viewState === "VIEW";
  const isInsertOrUpdate = ["INSERT", "UPDATE"].includes(viewState);

  const deptName = (() => {
    const code = editUserInfo?.depts?.primary?.code ?? editUserInfo.dept?.code;
    if (code === ROOT_DEPT_CODE) return companyName;
    return editUserInfo?.depts?.primary?.name ?? editUserInfo.dept?.name;
  })();

  return (
    <Box classes="adminDept-body-contents-body-userInfo" direction="column">
      {/* 아이디 */}
      <FieldRow
        label={t("admin.dept.userId")}
        required={viewState === "INSERT"}
      >
        {["VIEW", "UPDATE"].includes(viewState) ? (
          <ReadOnlyText
            value={
              viewState === "UPDATE"
                ? editUserInfo.id
                : userInfo?.id ?? userInfo?.userId
            }
          />
        ) : (
          <InputField
            value={editUserInfo.id}
            placeholder=""
            onChange={(e) =>
              setEditUserInfo({ ...editUserInfo, id: e.target.value })
            }
            width={240}
          />
        )}
      </FieldRow>

      {/* 이름 */}
      <FieldRow label={t("admin.dept.name")} required={isInsertOrUpdate}>
        {isView ? (
          <ReadOnlyText value={userInfo?.name ?? userInfo?.userName} />
        ) : (
          <InputField
            value={editUserInfo.name}
            placeholder=""
            onChange={(e) =>
              setEditUserInfo({ ...editUserInfo, name: e.target.value })
            }
            width={240}
          />
        )}
      </FieldRow>

      {/* 직책 (비OCI) */}
      {!isOCI && (
        <FieldRow
          label={t("admin.positionRole.position")}
          required={isInsertOrUpdate}
        >
          {isView ? (
            <ReadOnlyText value={userInfo.position?.name} />
          ) : (
            <Dropdown
              btnSize="sm"
              placeholder=" "
              width={240}
              buttonClick={buttonClick}
              value={editUserInfo.position?.name}
            >
              {positionList.map((item: PositionOrRoleDto) => (
                <DropdownItem
                  key={item.code}
                  label={item.name}
                  onClick={() => {
                    setEditUserInfo({ ...editUserInfo, position: item });
                    changeButtonClick(buttonClick + 1);
                  }}
                />
              ))}
            </Dropdown>
          )}
        </FieldRow>
      )}

      {/* 직무 (비OCI) */}
      {!isOCI && (
        <FieldRow
          label={t("admin.positionRole.job")}
          required={isInsertOrUpdate}
        >
          {isView ? (
            <ReadOnlyText value={userInfo.role?.name} />
          ) : (
            <Dropdown
              btnSize="sm"
              placeholder=" "
              width={240}
              buttonClick={buttonClick}
              value={editUserInfo.role?.name}
            >
              {roleList.map((item: PositionOrRoleDto) => (
                <DropdownItem
                  key={item.code}
                  label={item.name}
                  onClick={() => {
                    setEditUserInfo({ ...editUserInfo, role: item });
                    changeButtonClick(buttonClick + 1);
                  }}
                />
              ))}
            </Dropdown>
          )}
        </FieldRow>
      )}

      {/* 이메일 (비OCI) */}
      {!isOCI && (
        <FieldRow label={t("admin.dept.email")} required={isInsertOrUpdate}>
          {isView ? (
            <ReadOnlyText value={userInfo.email} />
          ) : (
            <InputField
              value={editUserInfo.email}
              placeholder=""
              onChange={(e) =>
                setEditUserInfo({ ...editUserInfo, email: e.target.value })
              }
              width={240}
            />
          )}
        </FieldRow>
      )}

      {/* 부서 (비OCI) */}
      {!isOCI && (
        <FieldRow label={t("admin.dept.dept")}>
          {isView ? (
            <ReadOnlyText value={userInfo.dept?.name} />
          ) : (
            <ReadOnlyText value={deptName} />
          )}
        </FieldRow>
      )}

      {/* OCI 전용: 회사명 */}
      {isOCI && (
        <FieldRow label={t("admin.dept.company")}>
          <ReadOnlyText value={userInfo.deptName} />
        </FieldRow>
      )}

      {/* OCI 전용: 가입일 */}
      {isOCI && (
        <FieldRow label={t("admin.manage.subscriptionDate")}>
          <ReadOnlyText
            value={utilsCommon.renderDateFormat(userInfo?.regDate)}
          />
        </FieldRow>
      )}

      {/* OCI 전용: 상태 */}
      {isOCI && (
        <FieldRow label={t("admin.dept.status")}>
          <ReadOnlyText
            value={
              userInfo.status === 1
                ? t("admin.manage.inUse")
                : userInfo.status === 2
                ? t("admin.manage.wait")
                : t("admin.manage.completedMember")
            }
          />
        </FieldRow>
      )}

      {/* 전화번호 (비OCI) */}
      {!isOCI && (
        <FieldRow label={t("admin.dept.phone")}>
          {isView ? (
            <ReadOnlyText value={userInfo.phoneNumber} />
          ) : (
            <InputField
              value={editUserInfo.phoneNumber}
              placeholder=""
              onChange={(e) =>
                setEditUserInfo({
                  ...editUserInfo,
                  phoneNumber: e.target.value,
                })
              }
              width={240}
            />
          )}
        </FieldRow>
      )}
      {!isOCI && (
        <FieldRow label={t("admin.dept.password")}>
          <PasswordSection
            viewState={viewState}
            isNewPassword={isNewPassword}
            setIsNewPassword={setIsNewPassword}
            password={password}
            sectionType="password"
            setPassword={setPassword}
            onChangePassword={onChangePassword}
          />
        </FieldRow>
      )}
      {/* 비밀번호 (비OCI) */}
      {!isOCI && isNewPassword && (
        <FieldRow label={t("admin.dept.settingMethod")} required>
          <PasswordSection
            viewState={viewState}
            isNewPassword={isNewPassword}
            setIsNewPassword={setIsNewPassword}
            password={password}
            sectionType="settingMethod"
            setPassword={setPassword}
            onChangePassword={onChangePassword}
          />
        </FieldRow>
      )}
      {!isOCI && (
        <PasswordSection
          viewState={viewState}
          isNewPassword={isNewPassword}
          setIsNewPassword={setIsNewPassword}
          password={password}
          sectionType="inputField"
          setPassword={setPassword}
          onChangePassword={onChangePassword}
        />
      )}
    </Box>
  );
};

// ────────────────────────────────────────────────────────────────────────────────
// 외부 사용자 폼
// ────────────────────────────────────────────────────────────────────────────────
const ExternalUserForm: React.FC<{
  viewState: ViewState;
  externalUserInfo: ExternalUser;
  editExternalUserInfo: ExternalUser;
  setEditExternalUserInfo: (v: ExternalUser) => void;
  password: PasswordState;
  setPassword: (v: PasswordState) => void;
  isNewPassword: boolean;
  setIsNewPassword: (v: boolean) => void;
  onChangePassword: () => void;
}> = ({
  viewState,
  externalUserInfo,
  editExternalUserInfo,
  setEditExternalUserInfo,
  password,
  setPassword,
  isNewPassword,
  setIsNewPassword,
  onChangePassword,
}) => {
  const { t } = useTranslation();
  const isOCI = window.eco.serviceType === "OCI";
  const isView = viewState === "VIEW";
  const isInsertOrUpdate = ["INSERT", "UPDATE"].includes(viewState);

  const canChangePassword =
    [1, 3].includes(externalUserInfo.status) &&
    externalUserInfo?.userType !== 3;

  return (
    <Box classes="adminDept-body-contents-body-userInfo" direction="column">
      {/* 이메일 (= userId) */}
      <FieldRow label={t("admin.dept.email")} required={viewState === "INSERT"}>
        {["VIEW", "UPDATE"].includes(viewState) ? (
          <ReadOnlyText
            value={externalUserInfo.userId ?? editExternalUserInfo.userId}
          />
        ) : (
          <InputField
            value={editExternalUserInfo.userId}
            placeholder=""
            onChange={(e) =>
              setEditExternalUserInfo({
                ...editExternalUserInfo,
                userId: e.target.value,
              })
            }
            width={240}
          />
        )}
      </FieldRow>
      {/* 이름 */}
      <FieldRow label={t("admin.dept.name")} required={isInsertOrUpdate}>
        {isView ? (
          <ReadOnlyText value={externalUserInfo.userName} />
        ) : (
          <InputField
            value={editExternalUserInfo.userName}
            placeholder=""
            onChange={(e) =>
              setEditExternalUserInfo({
                ...editExternalUserInfo,
                userName: e.target.value,
              })
            }
            width={240}
          />
        )}
      </FieldRow>
      {/* 회사 */}
      <FieldRow label={t("admin.dept.company")}>
        {isView ? (
          <ReadOnlyText value={externalUserInfo.deptName} />
        ) : (
          <InputField
            value={editExternalUserInfo.deptName}
            placeholder=""
            onChange={(e) =>
              setEditExternalUserInfo({
                ...editExternalUserInfo,
                deptName: e.target.value,
              })
            }
            width={240}
          />
        )}
      </FieldRow>
      {/* OCI: 상태 */}
      {isOCI && (
        <FieldRow label={t("admin.dept.status")}>
          <ReadOnlyText
            value={
              externalUserInfo.status === 1
                ? t("admin.manage.inUse")
                : externalUserInfo.status === 2
                ? t("admin.manage.wait")
                : t("admin.manage.completedMember")
            }
          />
        </FieldRow>
      )}
      {/* 전화번호 (비OCI) */}
      {!isOCI && (
        <FieldRow label={t("admin.dept.phone")}>
          {isView ? (
            <ReadOnlyText value={externalUserInfo.phoneNumber} />
          ) : (
            <InputField
              value={editExternalUserInfo.phoneNumber}
              placeholder=""
              onChange={(e) =>
                setEditExternalUserInfo({
                  ...editExternalUserInfo,
                  phoneNumber: e.target.value,
                })
              }
              width={240}
            />
          )}
        </FieldRow>
      )}
      {/* 기타 (비OCI) */}
      {!isOCI && (
        <FieldRow label={t("admin.dept.etc")}>
          {isView ? (
            <ReadOnlyText value={externalUserInfo.etc} />
          ) : (
            <InputField
              value={editExternalUserInfo.etc}
              placeholder=""
              onChange={(e) =>
                setEditExternalUserInfo({
                  ...editExternalUserInfo,
                  etc: e.target.value,
                })
              }
              width={240}
            />
          )}
        </FieldRow>
      )}
      {!isOCI && (
        <FieldRow label={t("admin.dept.password")}>
          <PasswordSection
            viewState={viewState}
            isNewPassword={isNewPassword}
            setIsNewPassword={setIsNewPassword}
            password={password}
            sectionType="password"
            setPassword={setPassword}
            onChangePassword={onChangePassword}
          />
        </FieldRow>
      )}
      {/* 비밀번호 (비OCI) */}
      {!isOCI && isNewPassword && (
        <FieldRow label={t("admin.dept.settingMethod")} required>
          <PasswordSection
            viewState={viewState}
            isNewPassword={isNewPassword}
            setIsNewPassword={setIsNewPassword}
            password={password}
            setPassword={setPassword}
            sectionType="settingMethod"
            onChangePassword={onChangePassword}
            showChangeButton={canChangePassword}
          />
        </FieldRow>
      )}
      {!isOCI && (
        <PasswordSection
          viewState={viewState}
          isNewPassword={isNewPassword}
          setIsNewPassword={setIsNewPassword}
          password={password}
          sectionType="inputField"
          setPassword={setPassword}
          onChangePassword={onChangePassword}
        />
      )}
    </Box>
  );
};

// ────────────────────────────────────────────────────────────────────────────────
// 메인 컴포넌트
// ────────────────────────────────────────────────────────────────────────────────
const UserInfoPanel: React.FC<Props> = ({
  viewState,
  nodeType,
  userInfo,
  editUserInfo,
  setEditUserInfo,
  externalUserInfo,
  editExternalUserInfo,
  setEditExternalUserInfo,
  positionList,
  roleList,
  buttonClick,
  changeButtonClick,
  password,
  setPassword,
  isNewPassword,
  setIsNewPassword,
  onChangePassword,
  ROOT_DEPT_CODE,
  companyName,
}) => {
  const { t } = useTranslation();

  return (
    <Box direction="column" classes="adminDept-body-contents-body">
      <Typography
        type="subtitle2"
        exactColor={getColor("COLOR_TEXT_SECONDARY")}
      >
        {t("admin.dept.userInfo")}
      </Typography>

      {nodeType === "user" && (
        <InternalUserForm
          viewState={viewState}
          userInfo={userInfo}
          editUserInfo={editUserInfo}
          setEditUserInfo={setEditUserInfo}
          positionList={positionList}
          roleList={roleList}
          buttonClick={buttonClick}
          changeButtonClick={changeButtonClick}
          password={password}
          setPassword={setPassword}
          isNewPassword={isNewPassword}
          setIsNewPassword={setIsNewPassword}
          onChangePassword={onChangePassword}
          ROOT_DEPT_CODE={ROOT_DEPT_CODE}
          companyName={companyName}
        />
      )}

      {nodeType === "externalUser" && (
        <ExternalUserForm
          viewState={viewState}
          externalUserInfo={externalUserInfo}
          editExternalUserInfo={editExternalUserInfo}
          setEditExternalUserInfo={setEditExternalUserInfo}
          password={password}
          setPassword={setPassword}
          isNewPassword={isNewPassword}
          setIsNewPassword={setIsNewPassword}
          onChangePassword={onChangePassword}
        />
      )}
    </Box>
  );
};

export default UserInfoPanel;
